import { ATTRIBUTION_MODEL, GRANULARITY } from '@adtrackify/at-tracking-event-types';

import { getAccountTimezone } from '@/store/account/timezone-cache';
import {
  getCompareDateRange,
  getDateRangeFromPreset,
} from '@/lib/helpers/dates/date-ranges';
import { DEFAULT_ATTRIBUTION_MODELS_MAP, DEFAULT_COMPARE_PRESET } from '../constants/dashboard-filter-constants';
import { ViewState } from '../types/dashboard-filter-types';

interface ViewDefaults {
  granularity: GRANULARITY;
  preset: string;
  attributionWindow: number;
  selectedMetric: string;
  pacing?: boolean;
  enableCompareDate?: boolean;
  includeToday?: boolean;
}

const VIEW_DEFAULTS: Record<string, ViewDefaults> = {
  'ltv-cohort-dashboard': {
    granularity: GRANULARITY.MONTH,
    preset: 'last12Months',
    attributionWindow: 30,
    selectedMetric: 'orders_ltv_cohort_net_revenue',
  },
  'rolling-reach': {
    granularity: GRANULARITY.DAY,
    preset: 'last30',
    attributionWindow: 30,
    selectedMetric: 'orders_customers_cohort',
    includeToday: false,
  },
  'cumulative-reach': {
    granularity: GRANULARITY.WEEK,
    preset: 'last6Months',
    attributionWindow: 30,
    selectedMetric: 'orders_customers_cohort',
    includeToday: false,
  },
  'reach-comparison': {
    granularity: GRANULARITY.DAY,
    preset: 'last30',
    attributionWindow: 30,
    selectedMetric: 'orders_customers_cohort',
    includeToday: false,
  },
};

const CUSTOM_DASHBOARD_PATHS = ['board/views', 'board/custom-dashboard'];

function isCustomDashboardPath(pathname: string): boolean {
  return CUSTOM_DASHBOARD_PATHS.some(p => pathname.includes(p));
}

function getViewDefaults(viewId?: string): ViewDefaults {
  // Check for exact viewId match first
  if (viewId && VIEW_DEFAULTS[viewId]) {
    return VIEW_DEFAULTS[viewId];
  }

  // Fall back to URL-based detection for backward compatibility
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';

  const isAttributionDashboard = pathname.includes('board/attribution');
  const isLtvCohortDashboard = pathname.includes('ltv-cohort');

  if (isLtvCohortDashboard) {
    return VIEW_DEFAULTS['ltv-cohort-dashboard'];
  }

  if (isAttributionDashboard) {
    return {
      granularity: GRANULARITY.DAY,
      preset: 'last7',
      attributionWindow: 7,
      selectedMetric: 'orders_customers_cohort',
      includeToday: false,
    };
  }

  if (isCustomDashboardPath(pathname)) {
    return {
      granularity: GRANULARITY.HOUR,
      preset: 'today',
      attributionWindow: 30,
      selectedMetric: 'orders_customers_cohort',
      pacing: false,
    };
  }

  // Generic defaults
  return {
    granularity: GRANULARITY.DAY,
    preset: 'last30',
    attributionWindow: 30,
    selectedMetric: 'orders_customers_cohort',
  };
}

export function getDefaultViewState(viewId?: string): ViewState {
  const timezone = getAccountTimezone();
  const defaults = getViewDefaults(viewId);

  const includeToday = defaults.includeToday ?? true;
  const initialDateRange = getDateRangeFromPreset(defaults.preset, timezone, includeToday);
  const compareEnabled = defaults.enableCompareDate ?? false;
  const initialCompareDateRange = compareEnabled
    ? getCompareDateRange(initialDateRange, DEFAULT_COMPARE_PRESET, timezone)
    : null;

  return {
    granularity: defaults.granularity,
    dateRange: initialDateRange,
    compareDateRange: initialCompareDateRange,
    enableCompareDate: compareEnabled,
    preset: defaults.preset,
    comparePreset: DEFAULT_COMPARE_PRESET,
    attributionModel: DEFAULT_ATTRIBUTION_MODELS_MAP[ATTRIBUTION_MODEL.FIRST_CLICK],
    attributionWindow: defaults.attributionWindow,
    selectedMetric: defaults.selectedMetric,
    includeToday,
    pacing: defaults.pacing ?? false,
    // Initialized so initializeViewFromExternalStore's `key in currentView`
    // gate accepts valueSource on first hydration. Non-attribution surfaces
    // ignore the field. Attribution view-sync overrides this with the saved
    // mode for Phase 1+ views and 'orders' for legacy views.
    valueSource: 'orders',
    // Canonical filter; `undefined` means no view filter applied.
    filters: undefined,
    refundDateAttribution: undefined,
  };
}
